import React, { useContext, useEffect, useState } from "react";
import {
  Box,
  Typography,
  Grid,
  Paper,
  CircularProgress,
  Alert,
  Button,
  TextField,
} from "@mui/material";
import { Getrequest, Postrequest } from "../../../../../../context/AxiosRequest";
import { ContextData } from "../../../../../../context/Context";
import { toWords } from "number-to-words";
import { toast } from "react-toastify";
import SoundAlert from "../../../../../../routes/SoundAlert";


export default function DisplayRequestLetterStudentLoan({ user_tokens,type ,from}) {




  const { UserTokens,accesstoken } = useContext(ContextData);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showReturnReason, setShowReturnReason] = useState(false);
  const [returnReason, setReturnReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const printRef = React.useRef(null);

  const handlePrint = async () => {
    const printContent = printRef.current;
    if (!printContent) {
      toast.error("Unable to generate PDF. Please try again.");
      return;
    }

    try {
      // Show loading notification
      toast.info("Generating PDF, please wait...");

      // Hide the download button before generating PDF
      const downloadButton = printContent.querySelector('.no-print');
      if (downloadButton) {
        downloadButton.style.display = 'none';
      }

      // Dynamically import html2pdf only when needed
      const html2pdf = (await import("html2pdf.js")).default;

      // PDF options
      const options = {
        margin: [10, 10, 10, 10],
        filename: `Request_Letter_${data?.clientName || 'Student'}_${new Date().getTime()}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { 
          scale: 2,
          useCORS: true,
          logging: false,
          letterRendering: true
        },
        jsPDF: { 
          unit: 'mm', 
          format: 'a4', 
          orientation: 'portrait'
        }
      };

      // Generate and download PDF
      await html2pdf()
        .set(options)
        .from(printContent)
        .save();
      
      toast.success("PDF downloaded successfully!");
      
      // Restore the button visibility
      if (downloadButton) {
        downloadButton.style.display = 'flex';
      }
    } catch (error) {
      console.error("PDF generation error:", error);
      toast.error("Failed to generate PDF. Please try again.");
      
      // Restore the button visibility even on error
      const downloadButton = printContent.querySelector('.no-print');
      if (downloadButton) {
        downloadButton.style.display = 'flex';
      }
    }
  };

  // Helper function to format amounts with commas
  const formatAmount = (amount) => {
    if (!amount || amount === "N/A") return "N/A";
    const numericValue = parseFloat(amount.replace(/,/g, ''));
    if (isNaN(numericValue)) return amount;
    return numericValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };


  // Handle Accept & Send Disbursement (for disbursement-submission)
  const handleAcceptDisbursement = async (user_token) => {

    try {

      setSubmitting(true);
      const response = await Postrequest("/auth/accept_student_request_letter",{ user_token: user_token,approver_status:"approved_disbursement" },
        accesstoken
      );

      if (response.status) {

        toast.success(response.textStatus || "Disbursement request accepted successfully");
        SoundAlert("success");

      } else {

        toast.error(response.textStatus || "Failed to accept disbursement request");
        SoundAlert("error");

      }

    } catch (err) {

      toast.error("An error occurred while processing the request");
      SoundAlert("error");
      console.error(err);

    } finally {
      setSubmitting(false);
    }


  };

  // Handle Accept Request Letter (for student-submission)
  const handleAcceptRequest = async (usertoken,totalAmountRequested,amountOffered) => {

           
                const AmountRequested = Number(totalAmountRequested.replace(/,/g, ''))
                 const offeredamount =  Number(amountOffered.replace(/,/g, '')) 

                if (AmountRequested > offeredamount) {

                  toast.warning("The requested amount exceeds the offered amount. Please review the request.");
                  SoundAlert("error");
                  return;

                }

    try {

      setSubmitting(true);
      const response = await Postrequest("/auth/accept_student_request_letter",{ user_token: usertoken, approver_status:"approved_request" },
        accesstoken
      );

      if (response.status) {

        toast.success(response.textStatus || "Request letter accepted successfully");
        SoundAlert("success");


      } else {

        toast.error(response.textStatus || "Failed to accept request letter");
        SoundAlert("error");

      }

      
    } catch (err) {


      toast.error("An error occurred while processing the request");
      SoundAlert("error");
      console.error(err);


    } finally {
      setSubmitting(false);
    }


  };

  // Handle Return Request Letter (for student-submission)
  const handleReturnRequest = async () => {

    if (!returnReason.trim()) {
      toast.warning("Please provide a reason for returning the request");
      SoundAlert("error");
      return;
    }

    try {
      
      setSubmitting(true);
      const response = await Postrequest(
        "/auth/accept_student_request_letter",
        { user_token: user_tokens, reason: returnReason, approver_status: "returned_request" },
        accesstoken
      );

      if (response.status) {

        toast.success(response.textStatus || "Request letter returned successfully");
        SoundAlert("success");
        setShowReturnReason(false);
        setReturnReason("");

      } else {

        toast.error(response.textStatus || "Failed to return request letter");
        SoundAlert("error");

      }
    } catch (err) {
      toast.error("An error occurred while processing the request");
      SoundAlert("error");
      console.error(err);
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    const fetchDisbursementRequest = async () => {
      try {
        setLoading(true);
        const response = await Getrequest(
          `/auth/get_disbursement_request/${user_tokens}`,
          accesstoken
        );

        if (response?.data) {
          setData(response.data);
        } else {
          setError("No disbursement request found");
        }
      } catch (err) {
        setError("Failed to fetch disbursement request");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    if (UserTokens) {
      fetchDisbursementRequest();
    }
  }, [UserTokens, accesstoken]);

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 2, maxWidth: "900px", mx: "auto" }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!data) {
    return (
      <Box sx={{ p: 2, maxWidth: "900px", mx: "auto" }}>
        <Alert severity="info">No disbursement request available</Alert>
      </Box>
    );
  }

    if (data.accept_request === "returned_request") {
    return (
      <Box sx={{ p: 2, maxWidth: "900px", mx: "auto" }}>
        <Alert severity="warning">This request has been returned.</Alert>
      </Box>
    );
  }
  return (
    <Box sx={{ marginTop: type === "disbursement-submission" ? "100em" : "80em" }}>
     

      {/* Printable Content */}
      <Box ref={printRef} className="!font-mono" sx={{ p: 2, maxWidth: "900px", mx: "auto" }}>

         {/* Download Button */}
         {/* dhhdhjskkkskks */}
      <Box 
        className="no-print"
        sx={{ 
          display: 'flex', 
          justifyContent: 'flex-end', 
          mb: 3, 
          mt: 30,
          mr: 2
        }}
      >  
        <Button 
          variant="contained" 
          color="primary"
          onClick={handlePrint}
          sx={{ 
            fontWeight: 'bold', 
            fontSize: { xs: '0.75rem', sm: '0.875rem', md: '1rem' }, 
            px: { xs: 2, sm: 3, md: 4 },
            py: { xs: 1, sm: 1.5 },
            borderRadius: 2,
            boxShadow: 3,
            textTransform: 'none',
            '&:hover': {
              boxShadow: 6,
              transform: 'translateY(-2px)',
              transition: 'all 0.3s ease'
            }
          }}
        >
          📥 Download Letter
        </Button>
      </Box>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: '20px' }}>
                  
                                      <img src="/newksslb.jpg" alt="KSSLB Logo" style={{ width: '100px', height: '100px', display: 'block', margin: '0 auto' , marginTop:'5em' }} />
                  
                                      <div className="mt-3">
                  
                                     <Typography sx={{ lineHeight: 1.9,fontFamily:"monospace",textAlign:"center" }} variant="h5" fontWeight="bold" gutterBottom>
                                    KADUNA STATE SCHOLARSHIP AND LOANS BOARD
                                   </Typography>   
                                   
                                   <Typography sx={{ lineHeight: 0.8,fontFamily:"monospace",textAlign:"center" }}  fontWeight="bold" gutterBottom>
                                   DISBURSEMENT & UTILIZATION REQUEST FORM {from !== "thirdparty" ? "(STUDENT LOAN)" : "(THIRD PARTY GUARANTEE)"}
                                   </Typography>  
                                   <Typography sx={{ lineHeight: 1.9,fontFamily:"monospace",textAlign:"center" }}  fontWeight="bold" gutterBottom>
                                    REQUEST FOR DISBURSEMENT AND UTILIZATION OF KADUNA STATE STUDENTS
                                    TERTIARY EDUCATION LOAN {from !== "thirdparty" ? "(STUDENT LOAN)" : "(THIRD PARTY GUARANTEE) "} 
                                   </Typography>
                                    
                                        
                                      </div>
                                   </div>
    {
      type === "disbursement-submission" && <>
      
    <Paper sx={{ p: 2, mb: 3 }}>
      
<address>
The Manager,<br/>
A.B.U. Microfinance Bank,<br/>
A.B.U. Samaru, <br/>
Zaria,Kaduna State.
</address>

    </Paper>

      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2}>

            
            <Typography  sx={{padding:"2em"}}  variant="body1" fontWeight=""> 
Kindly disburse and permit utilization of the loan amount of <span className="capitalize font-bold">{ toWords(Number(data.totalAmountRequested.replace(/,/g, ""))) } (N{data.totalAmountRequested})</span>  approved as follows:
            </Typography>

         
       
        </Grid>
      </Paper>  
      
      </>
    } 


      {/* Client Information */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2" color="text.secondary">
              Client's Name
            </Typography>
            <Typography variant="body1" fontWeight="bold">
              {data.clientName || "N/A"}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2" color="text.secondary">
              A.B.U Bank Account Number
            </Typography>
            <Typography variant="body1" fontWeight="bold">
              {data.loanbank_account_number || "N/A"}
            </Typography>
          </Grid>
        <Grid item xs={12}>
            <Typography variant="subtitle2" color="text.secondary">
             Amount Offered
            </Typography>
            <Typography variant="body1" fontWeight="bold">
              {data.amount_approve || "N/A"}
            </Typography>
          </Grid> 
          <Grid item xs={12}>
            <Typography variant="subtitle2" color="text.secondary">
              Disbursement Amount
            </Typography>
            <Typography variant="body1" fontWeight="bold">
              {data.disbursementAmountText || "N/A"}
            </Typography>
           
          </Grid>
          <Grid item xs={12}>
            <Typography variant="subtitle2" color="text.secondary">
              Student Name
            </Typography>
            <Typography variant="body1" fontWeight="bold">
              {data.disbursementStudentName || "N/A"}
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      {/* Payment Type */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Payment Type
        </Typography>
        <Typography variant="body1">
          {data.isInternational ? "International Payment" : "Local Payment"}
        </Typography>
      </Paper>

      {/* A(a) - Local Payment */}
      {!data.isInternational && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            A(a) - School Fees Payment (Local)
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" color="text.secondary">
                Account Name
              </Typography>
              <Typography variant="body1">{data.a_accountName || "N/A"}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" color="text.secondary">
                Remita
              </Typography>
              <Typography variant="body1">{data.a_remita || "N/A"}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" color="text.secondary">
                Amount
              </Typography>
              <Typography variant="body1">₦{formatAmount(data.a_amount)}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" color="text.secondary">
                Student Name
              </Typography>
              <Typography variant="body1">{data.a_studentName || "N/A"}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" color="text.secondary">
                Level
              </Typography>
              <Typography variant="body1">{data.a_level || "N/A"}</Typography>
            </Grid>
          </Grid>
        </Paper>
      )}

      {/* A(b) - Local Payment */}
      {!data.isInternational && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            A(b) - School Fees Payment Details
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" color="text.secondary">
                School Name
              </Typography>
              <Typography variant="body1">{data.b_schoolName || "N/A"}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" color="text.secondary">
                School Account Name
              </Typography>
              <Typography variant="body1">{data.b_schoolAccountName || "N/A"}</Typography>
            </Grid>  
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" color="text.secondary">
                School Account Number
              </Typography>
              <Typography variant="body1">{data.b_schoolAccountNumber || "N/A"}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" color="text.secondary">
                Bank
              </Typography>
              <Typography variant="body1">{data.b_bank || "N/A"}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" color="text.secondary">
                Student Name
              </Typography>
              <Typography variant="body1">{data.b_studentName || "N/A"}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" color="text.secondary">
                Level
              </Typography>
              <Typography variant="body1">{data.b_level || "N/A"}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" color="text.secondary">
                Matric Number
              </Typography>
              <Typography variant="body1">{data.b_matricNo || "N/A"}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" color="text.secondary">
                Course
              </Typography>
              <Typography variant="body1">{data.b_course || "N/A"}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" color="text.secondary">
                Amount
              </Typography>
              <Typography variant="body1">₦{formatAmount(data.b_amount)}</Typography>
            </Grid>
          </Grid>
        </Paper>
      )}

      {/* A(c) - International Payment */}
      {data.isInternational && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            A(c) - School Fees Payment (International)
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" color="text.secondary">
                School Name
              </Typography>
              <Typography variant="body1">{data.c_schoolName || "N/A"}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" color="text.secondary">
                School Account Name
              </Typography>
              <Typography variant="body1">{data.c_schoolAccountName || "N/A"}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" color="text.secondary">
                School Account Number
              </Typography>
              <Typography variant="body1">{data.c_schoolAccountNumber || "N/A"}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" color="text.secondary">
                Amount
              </Typography>
              <Typography variant="body1">₦{formatAmount(data.c_amount)}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" color="text.secondary">
                Bank
              </Typography>
              <Typography variant="body1">{data.c_bank || "N/A"}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" color="text.secondary">
                Course
              </Typography>
              <Typography variant="body1">{data.c_course || "N/A"}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" color="text.secondary">
                IBAN
              </Typography>
              <Typography variant="body1">{data.c_iban || "N/A"}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" color="text.secondary">
                Swift Code
              </Typography>
              <Typography variant="body1">{data.c_swift || "N/A"}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" color="text.secondary">
                Branch
              </Typography>
              <Typography variant="body1">{data.c_branch || "N/A"}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" color="text.secondary">
                Matric Number
              </Typography>
              <Typography variant="body1">{data.c_matricNo || "N/A"}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" color="text.secondary">
                Student Name
              </Typography>
              <Typography variant="body1">{data.c_studentName || "N/A"}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" color="text.secondary">
                Level
              </Typography>
              <Typography variant="body1">{data.c_level || "N/A"}</Typography>
            </Grid>
          </Grid>
        </Paper>
      )}

      {/* Upkeep */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          B - Upkeep
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2" color="text.secondary">
              Student Account Name
            </Typography>
            <Typography variant="body1">{data.up_name || "N/A"}</Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2" color="text.secondary">
              Account Number
            </Typography>
            <Typography variant="body1">{data.up_accNo || "N/A"}</Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2" color="text.secondary">
              Bank
            </Typography>
            <Typography variant="body1">{data.up_bank || "N/A"}</Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2" color="text.secondary">
              Amount
            </Typography>
            <Typography variant="body1">₦{formatAmount(data.up_amount)}</Typography>
          </Grid>
        </Grid>
      </Paper>

      {/* Skills Acquisition */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          C - Skills Acquisition
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2" color="text.secondary">
              Centre Name
            </Typography>
            <Typography variant="body1">{data.sk_centre || "N/A"}</Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2" color="text.secondary">
              Centre Account Name
            </Typography>
            <Typography variant="body1">{data.sk_centreAcc || "N/A"}</Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2" color="text.secondary">
              Account Number
            </Typography>
            <Typography variant="body1">{data.sk_accNo || "N/A"}</Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2" color="text.secondary">
              Bank
            </Typography>
            <Typography variant="body1">{data.sk_bank || "N/A"}</Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2" color="text.secondary">
              Amount
            </Typography>
            <Typography variant="body1">₦{formatAmount(data.sk_amount)}</Typography>
          </Grid>
        </Grid>
      </Paper>

      {/* Others */}
      {data.hasOthers && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            D - Others
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" color="text.secondary">
                Account Name
              </Typography>
              <Typography variant="body1">{data.oth_name || "N/A"}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" color="text.secondary">
                Account Number
              </Typography>
              <Typography variant="body1">{data.oth_accNo || "N/A"}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" color="text.secondary">
                Bank
              </Typography>
              <Typography variant="body1">{data.oth_bank || "N/A"}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" color="text.secondary">
                Amount
              </Typography>
              <Typography variant="body1">₦{formatAmount(data.oth_amount)}</Typography>
            </Grid>
          </Grid>
        </Paper>
      )}

      {/* Signature Section */}
      <Box sx={{ mt: 6, mb: 4 }}>
        <Grid container spacing={4}>

          
           {
             type === "disbursement-submission" && 

              <>
              {/* Left Signature */}
          <Grid item xs={12} sm={6}>
            <Box sx={{ textAlign: 'center',marginTop:"2em" }}>
              <Box 
                sx={{ 
                  width: '200px', 
                  height: '80px', 
                  border: '1px dashed #ccc',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto',
                  mb: 1
                }}
              >
                <Typography variant="caption" sx={{marginBottom:"3em"}} color="text.secondary">
                  <img src="/assets/es-sign.jpeg" alt="Signature" style={{ maxWidth: '100%', maxHeight: '100%' }} />
                </Typography>
              </Box>
              <Typography variant="body1" fontWeight="bold">
                Prof. Yahya Saleh Ibrahim
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Executive Secretary
              </Typography>
            </Box>
          </Grid>

             {/* Right Signature */}
          <Grid item xs={12} sm={6}>
            <Box sx={{ textAlign: 'center',marginTop:"2em" }}>
              <Box 
                sx={{ 
                  width: '200px', 
                  height: '80px', 
                  border: '1px dashed #ccc',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto',
                  mb: 1
                }}
              >
               <Typography variant="caption" sx={{marginBottom:"3em"}} color="text.secondary">
                  <img src="/assets/iyalsign.png" alt="Signature" style={{ maxWidth: '100%', maxHeight: '100%' }} />
                </Typography>
              </Box>
              <Typography variant="body1" fontWeight="bold">
                Liman Iyal Zubair
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Director of Loans Department
              </Typography>
            </Box>
          </Grid>
              </>
           } 
           </Grid>
      </Box>

      {/* Action Buttons Section */}
      
      {type && (
        <Box sx={{ mt: 4, mb: 4 }}>
            
          {type === "disbursement-submission" ? (
            // Single button for disbursement-submission
              
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                 
                 {
                  data.approver_status === "approved_disbursement" ?
                  <Alert severity="success" sx={{ mb: 3 }}>
                  This disbursement request has already been sent.
                </Alert>
                :

               <Button
                variant="contained"
                color="primary"
                size="large"
                onClick={() => handleAcceptDisbursement(data.user_token)}
                disabled={submitting}
                sx={{ minWidth: '250px' }}
                >
                {submitting ? "Processing..." : "Accept & Send Disbursement"}
                </Button>

                 }



            </Box>

          ) : type === "student-submission" ? (
            // Two buttons for student-submission
            <Box>
            
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mb: 3 }}>
                {
                   data.accept_request === "approved_request" ?

                 <Alert severity="success" sx={{ mb: 3 }}>
                   This request letter has already been accepted.
                 </Alert>
                 : 
                 <>
                  
                 <Button
                  variant="contained"
                  color="success"
                  size="large"
                  onClick={() => handleAcceptRequest(data.user_token,data.totalAmountRequested,data.amount_approve)}
                  disabled={submitting || showReturnReason}
                  sx={{ minWidth: '200px' }}
                >
                   
                  {submitting ? "Processing..." : "Accept Request Letter"}

                 </Button>

                <Button
                  variant="contained"
                  color="warning"
                  size="large"
                  onClick={() => setShowReturnReason(!showReturnReason)}
                  disabled={submitting}
                  sx={{ minWidth: '200px' }}
                >
                  Return Request Letter
                </Button>
                 
                 </>


                }
               
              </Box>

              {/* Return Reason Section */}
              {showReturnReason && (
                <Paper sx={{ p: 3, mt: 2 }}>
                  <Typography variant="h6" gutterBottom>
                    Reason for Return
                  </Typography>
                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    label="Enter reason"
                    value={returnReason}
                    onChange={(e) => setReturnReason(e.target.value)}
                    placeholder="Please provide a detailed reason for returning this request letter..."
                    sx={{ mb: 2 }}
                  />
                  <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                    <Button
                      variant="outlined"
                      onClick={() => {
                        setShowReturnReason(false);
                        setReturnReason("");
                      }}
                      disabled={submitting}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="contained"
                      color="error"
                      onClick={() => handleReturnRequest(data.user_token)}
                      disabled={submitting || !returnReason.trim()}
                    >
                      {submitting ? "Submitting..." : "Submit Return"}
                    </Button>
                  </Box>
                </Paper>
              )}
            </Box>
          ) : null}
        </Box>
      )}
      </Box>
    </Box>
  );
}
